import {
  MAX_TEXT_LENGTH,
  MAX_TEXT_PHOTOS,
  byDay,
  defaultTextLine,
  formatPhone,
  mayDeletePersonalText,
  mergeThread,
  personalPhoneLabel,
  photoProblem,
  primaryPhone,
  textCost,
  textLines,
  toE164,
  type Job,
  type TextFrom,
} from "@roof-hawk/core";
import { useEffect, useMemo, useRef, useState } from "react";

import { ErrorNote } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import {
  deletePersonalText,
  fetchContactMessages,
  fetchContactPersonalTexts,
  mayTextNumber,
  sendText,
  setContactPersonalTexts,
} from "../../lib/queries";
import { openSms } from "../../lib/sendChannel";
import { uploadTextPhoto } from "../../lib/textPhotos";
import { PersonalBubble, TextBubble, dayLabel } from "./TextBubbles";
import { FromPicker } from "./TextLinePicker";
import { PickedPhotos, type PickedPhoto } from "./TextPhotos";
import { useSession } from "../../session";

/**
 * The Texts card on a job.
 *
 * All four Twilio numbers can receive a message since the campaign was approved
 * on 2026-08-23, and until this screen existed every one of those messages was
 * something only Twilio could see. This is where they are read and answered.
 *
 * Three decisions worth knowing about:
 *
 * **The thread is the customer's, not the job's.** Somebody with a roof job and
 * a gutter job did not have two conversations. A reply is still filed against
 * the job it was typed on, so the timeline on this job says what was said.
 *
 * **It is one thread, not two.** Since 2026-08-30, texts synced from a rep's
 * own phone are merged into it by time and badged with the phone they came
 * through (ADR 0090, ADR 0178). Two sections would put the answer to "what did
 * we last tell them" in whichever one nobody read.
 *
 * **A picture goes the same way as words** (ADR 0310). Jonathon, 2026-10-24:
 * "we need to be able to send and receive photos via text." Up to five, each
 * shrunk to a size a carrier delivers before it is uploaded, previewed under
 * the box, and sent from the Roof Board line — never from a rep's own phone,
 * which Messages cannot be handed a picture for.
 *
 * **A Roof Board reply goes out from the Roof Board line**, (205) 413-4263.
 * That is still the default and still what every rep gets. The one person whose
 * phone syncs also gets the choice to send from it, and that path sends nothing
 * — it hands the message to Messages and they tap send themselves.
 */
export function TextsPanel({
  job,
  line: chosenLine = null,
  onLine,
}: {
  job: Job;
  /**
   * The line to send from, when the phone bubble's own picker holds the
   * choice (ADR 0321): then this card's dropdown shows that choice and a
   * change here is reported back, so a call and a text leave from one number.
   * Left out, the card keeps its own choice as before.
   */
  line?: string | null;
  onLine?: (line: string) => void;
}) {
  const { profile, people } = useSession();
  // The whole personal-text feature is one marker on one profile. Everybody
  // else sees this card exactly as it was.
  const mine = profile?.syncs_personal_texts === true;

  const contactId = job.contact_id;
  const number = primaryPhone(job.contact);

  const messages = useAsync(
    () => (contactId ? fetchContactMessages(contactId) : Promise.resolve([])),
    [contactId],
  );

  const personal = useAsync(
    () =>
      contactId ? fetchContactPersonalTexts(contactId) : Promise.resolve([]),
    [contactId],
  );

  // Whether this number may be texted is the database's answer, not this
  // screen's guess: the opt-out list is the same one the send checks. It gates
  // the personal-cell path too — somebody who replied STOP has told this
  // company to stop, and which handset the next message would leave from is not
  // the distinction they were drawing.
  const allowed = useAsync(
    () =>
      number
        ? mayTextNumber(number, contactId)
        : Promise.resolve({
            ok: false,
            reason: t("This contact has no phone number."),
          }),
    [number, contactId],
  );

  const [draft, setDraft] = useState("");
  const [from, setFrom] = useState<TextFrom>("roof-board");
  // Which of our numbers it leaves from (ADR 0310). Jonathon, 2026-10-24:
  // "there needs to be a drop down so we can see and choose which number
  // we're texting from. defaulting to our own of course." The list is the
  // staff's lines, own first; the choice is kept while the card is open.
  const lines = useMemo(
    () => textLines(people ?? [], profile?.id),
    [people, profile?.id],
  );
  const [ownPick, setOwnPick] = useState<string | null>(null);
  const fromLine = chosenLine ?? ownPick ?? defaultTextLine(lines);
  const setLine = (next: string) => {
    setOwnPick(next);
    onLine?.(next);
  };
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [excluded, setExcluded] = useState(
    job.contact?.exclude_personal_texts === true,
  );
  const [photos, setPhotos] = useState<PickedPhoto[]>([]);
  const thread = useRef<HTMLDivElement>(null);
  const picker = useRef<HTMLInputElement>(null);

  const entries = useMemo(
    () => mergeThread(messages.data ?? [], personal.data ?? []),
    [messages.data, personal.data],
  );

  // Whose phone each text came through, by id. From the staff list the session
  // already holds rather than a join on every text: two phones today, and a
  // name that has been on screen since the app loaded.
  const nameOf = useMemo(() => {
    const names = new Map<string, string>();
    for (const person of people ?? [])
      if (person.full_name) names.set(person.id, person.full_name);
    return (id: string | null) => (id ? (names.get(id) ?? null) : null);
  }, [people]);
  const days = useMemo(() => byDay(entries), [entries]);

  // Say which line a text left from only once the thread has used more than
  // one: a conversation all on one number has nothing to tell.
  const severalLines = useMemo(
    () =>
      new Set(
        (messages.data ?? [])
          .filter((m) => m.direction === "outbound" && m.from_number)
          .map((m) => m.from_number),
      ).size > 1,
    [messages.data],
  );
  const lineName = (number: string | null | undefined) =>
    lines.find((l) => l.number === number)?.label.split(" · ")[0] ??
    formatPhone(number?.replace(/^\+1/, "")) ??
    null;
  const cost = textCost(draft);

  // Open on the newest, the way every messaging app does. A rep coming to this
  // card wants the last thing that was said; landing them at the top of a
  // two-year conversation and asking them to scroll is the difference between
  // a thread that gets read and one that does not.
  //
  // Not smooth on first paint — `scrollTo` with the container's own
  // `scroll-behavior` would animate a long thread past the whole history on
  // load. Jumping is what a phone does.
  useEffect(() => {
    const box = thread.current;
    if (!box) return;
    // `behavior: "instant"` is load-bearing, not a preference. Assigning
    // `scrollTop` against a container with `scroll-behavior: smooth` is
    // swallowed — the element never moves — and the card opens on the oldest
    // message in the conversation. Saying it here means the CSS cannot take it
    // away again.
    box.scrollTo({ top: box.scrollHeight, behavior: "instant" });
  }, [entries]);

  // A picture on its own is a message; words on their own always were.
  const hasSomething = draft.trim() !== "" || photos.length > 0;
  const canSend =
    allowed.data?.ok === true && hasSomething && !sending && !!number;

  function addPhotos(files: FileList | null) {
    if (!files) return;
    setError(null);
    const picked: PickedPhoto[] = [];
    for (const file of Array.from(files)) {
      const problem = photoProblem(file, photos.length + picked.length);
      if (problem) {
        setError(t(problem));
        break;
      }
      picked.push({
        id: crypto.randomUUID(),
        file,
        preview: URL.createObjectURL(file),
      });
    }
    if (picked.length) setPhotos((have) => [...have, ...picked]);
    if (picker.current) picker.current.value = "";
  }

  function dropPhoto(id: string) {
    setPhotos((have) => {
      const gone = have.find((p) => p.id === id);
      if (gone) URL.revokeObjectURL(gone.preview);
      return have.filter((p) => p.id !== id);
    });
  }

  async function send() {
    if (!canSend || !number) return;

    // Sending from the rep's own phone is not a send at all: Roof Board opens
    // Messages with the text in it and stops. There is no confirmation because
    // there is nothing to confirm — whether they tapped the blue arrow is
    // unknowable from here, and the message turning up in this thread a minute
    // later, via the Mac sync, is the only honest confirmation there is.
    if (from === "cell") {
      if (photos.length > 0) {
        setError(
          t(
            "Pictures go from the Roof Board line. Switch the sender, or take the pictures off.",
          ),
        );
        return;
      }
      const outcome = openSms(toE164(number), draft.trim());
      if (outcome.state === "refused") setError(outcome.reason);
      else setDraft("");
      return;
    }

    setSending(true);
    setError(null);
    try {
      // Each picture is shrunk and uploaded first; the send names the paths.
      const mediaPaths: string[] = [];
      for (const photo of photos)
        mediaPaths.push(await uploadTextPhoto(photo.file));
      const sent = await sendText({
        to: number,
        body: draft.trim(),
        jobId: job.id,
        contactId,
        ...(mediaPaths.length ? { mediaPaths } : {}),
        ...(fromLine ? { from: fromLine } : {}),
      });
      setDraft("");
      for (const photo of photos) URL.revokeObjectURL(photo.preview);
      setPhotos([]);
      if (sent.warning) setError(sent.warning);
      await messages.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSending(false);
    }
  }

  async function toggleExclusion() {
    const next = !excluded;
    if (
      next &&
      !window.confirm(
        t(
          "Stop syncing texts with this person, and remove the ones already here?",
        ),
      )
    ) {
      return;
    }
    try {
      setExcluded(await setContactPersonalTexts(contactId!, next));
      await personal.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function remove(id: string) {
    if (!window.confirm(t("Remove this text from the customer's profile?")))
      return;
    try {
      await deletePersonalText(id);
      await personal.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  if (!number) {
    return (
      <div className="block">
        <div className="tnone">
          {t("This contact has no phone number, so there is nobody to text.")}
        </div>
      </div>
    );
  }

  return (
    <div className="block">
      <h2>
        {t("Texts")}{" "}
        <span className="na-hint">
          {t("with {name}", {
            name: job.contact?.full_name ?? formatPhone(number) ?? "",
          })}
        </span>
      </h2>

      <ErrorNote error={messages.error ?? personal.error ?? error} />

      {mine && contactId && (
        // Some people in `contacts` are not customers, and some customers'
        // conversations are not the company's business. Excluding is not a
        // filter on the way to this screen — the ingest stops storing them at
        // all, which is the only version of "do not track" worth having.
        <p className="na-hint" style={{ marginBottom: 8 }}>
          {excluded
            ? t(
                "Texts from your own phone with this person are not being synced.",
              )
            : t(
                "Texts from your own phone with this person are synced here.",
              )}{" "}
          <button
            type="button"
            className="linkbtn"
            onClick={() => void toggleExclusion()}
          >
            {excluded ? t("Start syncing them") : t("Stop syncing them")}
          </button>
        </p>
      )}

      <div className="textthread" ref={thread}>
        {entries.length === 0 ? (
          <div className="tnone">
            {t(
              "Nothing yet. A text sent from here goes out from the Roof Board line, so their reply comes back to this conversation.",
            )}
          </div>
        ) : (
          days.map((day) => (
            // `textdaygroup` is a flex column of its own. Without it the
            // bubbles are not children of the thread's flex box, `align-self`
            // does nothing, and every message stretches the full width — which
            // is what the left/right split is supposed to be saying.
            <div className="textdaygroup" key={day.day}>
              <div className="textday">{dayLabel(day.day)}</div>
              {day.messages.map((entry) =>
                entry.channel === "personal" ? (
                  <PersonalBubble
                    key={entry.key}
                    entry={entry}
                    owner={t(
                      personalPhoneLabel({
                        outbound: entry.mine,
                        ownerName: nameOf(entry.text.source_profile_id),
                        ownPhone: entry.text.source_profile_id === profile?.id,
                        contactName: job.contact?.full_name,
                      }),
                    )}
                    onDelete={
                      mayDeletePersonalText(entry.text, profile)
                        ? () => void remove(entry.text.id)
                        : null
                    }
                  />
                ) : (
                  <TextBubble
                    key={entry.key}
                    message={entry.message}
                    line={
                      severalLines && entry.message.direction === "outbound"
                        ? lineName(entry.message.from_number)
                        : null
                    }
                  />
                ),
              )}
            </div>
          ))
        )}
      </div>

      {allowed.data && !allowed.data.ok ? (
        // Not a disabled box with no explanation: the reason is the whole
        // point, and STOP is a rule rather than a setting somebody can undo.
        <div className="err" role="status" style={{ marginTop: 12 }}>
          {allowed.data.reason}
        </div>
      ) : (
        <div className="textcompose">
          {mine && (
            <FromPicker value={from} onChange={setFrom} number={number} />
          )}
          {from === "roof-board" && lines.length > 0 && (
            <label className="linepicker">
              {t("From")}{" "}
              <select
                aria-label={t("From line")}
                value={fromLine ?? ""}
                onChange={(e) => setLine(e.target.value)}
              >
                {lines.map((option) => (
                  <option key={option.number} value={option.number}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          )}
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value.slice(0, MAX_TEXT_LENGTH))}
            placeholder={t("Text {number}…", {
              number: formatPhone(number) ?? "",
            })}
            rows={3}
            aria-label={t("Message")}
          />
          <PickedPhotos photos={photos} onDrop={dropPhoto} />
          <div className="textsend actions">
            <input
              ref={picker}
              type="file"
              accept="image/*"
              multiple
              hidden
              aria-label={t("Pictures")}
              onChange={(e) => addPhotos(e.target.files)}
            />
            <button
              type="button"
              className="addrow ghost"
              disabled={from === "cell" || photos.length >= MAX_TEXT_PHOTOS}
              title={
                from === "cell"
                  ? t("Pictures go from the Roof Board line")
                  : t("Add a picture to this text")
              }
              onClick={() => picker.current?.click()}
            >
              {t("+ Picture")}
            </button>
            <span className="na-hint">
              {cost.segments > 1
                ? t("{characters} characters · {segments} texts", {
                    characters: cost.characters,
                    segments: cost.segments,
                  })
                : t("{n} characters", { n: cost.characters })}
              {cost.unicode && cost.segments > 0
                ? ` · ${t("emoji shorten a text")}`
                : ""}
            </span>
            <button
              className="btn-primary"
              disabled={!canSend}
              // Fire and forget: send() shows its own errors.
              onClick={() => void send()}
            >
              {from === "cell"
                ? t("Open Messages")
                : sending
                  ? t("Sending…")
                  : t("Send")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
